import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Filter, Info, Loader2, Package, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VirtualizedStringCombobox } from "@/components/ui/virtualized-string-combobox";
import { productGroupsApi } from "@/features/grupy-produktow/api";
import {
	buildCategoryTree,
	CategoryFilterCombobox,
} from "@/features/products/components/category-filter-combobox";
import {
	attemptDynamicImportRecovery,
	describeDynamicImportError,
	loadXlsx,
} from "@/lib/dynamic-import";
import { cn } from "@/lib/utils";
import {
	BLOZ_FORMAT,
	buildImportSummaryToast,
	extractSingleColumnFromSheet,
	type ImportReconciliation,
	ImportReportPanel,
	reconcileCodeImport,
} from "./code-import-report";
import { contractsApi, type ProductItem } from "./contracts-api";

const EMPTY_SELECTION: string[] = [];
const PAGE_SIZE = 50;

interface ProductSelectorModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelect: (products: ProductItem[]) => void;
	multiSelect?: boolean;
	title?: string;
	initialSelection?: string[];
}

export function ProductSelectorModal({
	open,
	onOpenChange,
	onSelect,
	multiSelect = true,
	title = "Dodaj produkty",
	initialSelection,
}: ProductSelectorModalProps) {
	const selectedFromParent = initialSelection ?? EMPTY_SELECTION;
	const [searchQuery, setSearchQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [selectedProducer, setSelectedProducer] = useState<string>("");
	const [selectedSubstance, setSelectedSubstance] = useState<string>("");
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
	const [selectedProductGroupId, setSelectedProductGroupId] = useState<number | null>(null);
	const [isImportingFromFile, setIsImportingFromFile] = useState(false);
	// Podsumowanie ostatniego importu z pliku — toast znika, a użytkownik musi
	// móc odczytać i skopiować kody, które nie weszły do zaznaczenia.
	const [importReport, setImportReport] = useState<ImportReconciliation | null>(null);

	// Map of bloz07 -> ProductItem for efficient management of multiple selections
	const [selectedProducts, setSelectedProducts] = useState<Map<string, ProductItem>>(new Map());

	// Świeże otwarcie modala = świeży import; stare podsumowanie wprowadzałoby w błąd.
	useEffect(() => {
		if (!open) setImportReport(null);
	}, [open]);

	// Debounce search to avoid overwhelming the API
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Synchronize selected products with parent state on each open.
	useEffect(() => {
		if (!open) return;
		if (selectedFromParent.length === 0) {
			setSelectedProducts(new Map());
			return;
		}

		contractsApi.searchByBlozList(selectedFromParent).then((data) => {
			const next = new Map<string, ProductItem>();
			for (const product of data) {
				next.set(product.bloz07, product);
			}
			setSelectedProducts(next);
		});
	}, [open, selectedFromParent]);

	// Dictionaries for filters (30 mins stale time)
	const { data: producers = [] } = useQuery({
		queryKey: ["products", "producers"],
		queryFn: contractsApi.getProducers,
		staleTime: 1000 * 60 * 30,
		enabled: open,
	});

	const { data: substances = [] } = useQuery({
		queryKey: ["products", "substances"],
		queryFn: contractsApi.getSubstances,
		staleTime: 1000 * 60 * 30,
		enabled: open,
	});

	const { data: categoryTreeData = [] } = useQuery({
		queryKey: ["categories", "tree"],
		queryFn: contractsApi.getCategoryTree,
		staleTime: 1000 * 60 * 30,
		enabled: open,
	});

	const categoryOptions = useMemo(() => buildCategoryTree(categoryTreeData), [categoryTreeData]);

	// Farmasterowe grupy produktow (per-org) — dla selektora filtra.
	const { data: productGroups = [] } = useQuery({
		queryKey: ["product-groups", "list"],
		queryFn: productGroupsApi.list,
		staleTime: 1000 * 60 * 5,
		enabled: open,
	});

	// Main search query z infinite scroll — strony po PAGE_SIZE wynikow
	const {
		data: searchResult,
		isFetching,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: [
			"products",
			"search",
			debouncedQuery,
			selectedProducer,
			selectedSubstance,
			selectedCategory,
			selectedProductGroupId,
		],
		queryFn: ({ pageParam }) =>
			contractsApi.searchProducts({
				q: debouncedQuery || undefined,
				producer: selectedProducer || undefined,
				substance: selectedSubstance || undefined,
				category: selectedCategory,
				productGroupId: selectedProductGroupId ?? undefined,
				limit: PAGE_SIZE,
				offset: pageParam,
			}),
		initialPageParam: 0,
		getNextPageParam: (_lastPage, allPages) => {
			const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
			const total = allPages[0]?.total ?? 0;
			return loaded < total ? loaded : undefined;
		},
		enabled:
			open &&
			(debouncedQuery.length >= 2 ||
				selectedProducer !== "" ||
				selectedSubstance !== "" ||
				selectedCategory !== undefined ||
				selectedProductGroupId !== null),
	});
	const products = useMemo(() => searchResult?.pages.flatMap((p) => p.items) ?? [], [searchResult]);
	const totalCount = searchResult?.pages[0]?.total ?? 0;

	const [isSelectingAll, setIsSelectingAll] = useState(false);

	// Toggle "zaznacz wszystkie" — fetchuje WSZYSTKIE pasujace produkty (nie tylko
	// zaladowane do tabeli), zeby checkbox pokrywal caly dataset a nie tylko
	// obecnie zrenderowane wiersze. Backend ma max limit 500 per request, wiec
	// przy wiekszych zestawach robimy pętle offset+500.
	const handleToggleSelectAll = async (checked: boolean) => {
		if (totalCount === 0) return;
		setIsSelectingAll(true);
		try {
			const all: ProductItem[] = [];
			let offset = 0;
			const pageSize = 500;
			while (true) {
				const res = await contractsApi.searchProducts({
					q: debouncedQuery || undefined,
					producer: selectedProducer || undefined,
					substance: selectedSubstance || undefined,
					category: selectedCategory,
					productGroupId: selectedProductGroupId ?? undefined,
					limit: pageSize,
					offset,
				});
				all.push(...res.items);
				offset += res.items.length;
				if (offset >= res.total || res.items.length === 0) break;
			}
			setSelectedProducts((prev) => {
				const next = new Map(prev);
				if (checked) {
					for (const p of all) next.set(p.bloz07, p);
				} else {
					for (const p of all) next.delete(p.bloz07);
				}
				return next;
			});
		} catch (err) {
			console.error("Błąd podczas zaznaczania wszystkich:", err);
		} finally {
			setIsSelectingAll(false);
		}
	};

	// Sentinel dla infinite scroll — gdy staje sie widoczny, laduje nastepna strone
	const sentinelRef = useRef<HTMLTableRowElement>(null);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1, rootMargin: "100px" },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Search by BLOZ list (Import from file functionality)
	const importMutation = useMutation({
		mutationFn: contractsApi.searchByBlozList,
		onSuccess: (data) => {
			setSelectedProducts((prev) => {
				const next = new Map(prev);
				for (const p of data) {
					next.set(p.bloz07, p);
				}
				return next;
			});
		},
	});

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const input = e.target;
		const file = input.files?.[0];
		if (!file) return;

		const lowerName = file.name.toLowerCase();
		const isXlsx = lowerName.endsWith(".xlsx") || lowerName.endsWith(".xls");
		if (!isXlsx) {
			input.value = "";
			return;
		}

		const reader = new FileReader();
		setIsImportingFromFile(true);
		reader.onload = async (ev) => {
			try {
				// Dynamic import — xlsx ładuje się tylko gdy user wybiera plik.
				// loadXlsx centralizuje import-target — stale-chunk error trafia do catch.
				const XLSX = await loadXlsx();
				const data = ev.target?.result;
				if (!(data instanceof ArrayBuffer)) {
					throw new Error("Nie udało się odczytać pliku XLSX.");
				}
				const workbook = XLSX.read(new Uint8Array(data), { type: "array" });
				const worksheet = workbook.Sheets[workbook.SheetNames[0]];
				const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
				const extracted = extractSingleColumnFromSheet(rows, BLOZ_FORMAT);

				// Wiele kolumn = nie zgadujemy, która trzyma BLOZ. Odrzucamy cały plik,
				// zamiast zaimportować "coś" z przypadkowej kolumny.
				if (extracted.columns > 1) {
					setImportReport(null);
					toast.error(`Import przyjmuje plik z jedną kolumną, a ten ma ich ${extracted.columns}.`, {
						description: "Zostaw w pliku wyłącznie kolumnę z BLOZ (7 cyfr) i wczytaj ponownie.",
						duration: 10000,
					});
					return;
				}

				if (extracted.codes.length === 0) {
					// Kody o złym formacie i tak trzeba pokazać — inaczej plik z samymi
					// błędnymi wartościami wygląda jak plik bez kodów.
					if (extracted.invalid.length > 0) {
						setImportReport(reconcileCodeImport([], [], extracted.invalid));
					}
					toast.warning("Nie znaleziono w pliku żadnego kodu BLOZ (7 cyfr).");
					return;
				}

				const matched = await importMutation.mutateAsync(Array.from(new Set(extracted.codes)));
				const report = reconcileCodeImport(
					extracted.codes,
					matched.map((product) => product.bloz07),
					extracted.invalid,
				);
				setImportReport(report);
				const summary = buildImportSummaryToast(report, report.duplicatesRemoved, BLOZ_FORMAT);
				const notify = summary.level === "warning" ? toast.warning : toast.success;
				notify(summary.message, {
					description: summary.description,
					duration: summary.level === "warning" ? 10000 : undefined,
				});
			} catch (err) {
				if (attemptDynamicImportRecovery(err)) return;
				console.error("Błąd podczas czytania pliku:", err);
				toast.error(describeDynamicImportError(err, "Błąd podczas czytania pliku"));
			} finally {
				setIsImportingFromFile(false);
				input.value = "";
			}
		};
		reader.onerror = () => {
			console.error("Błąd podczas odczytu pliku.");
			setIsImportingFromFile(false);
			input.value = "";
		};

		reader.readAsArrayBuffer(file);
	};

	const toggleProduct = (product: ProductItem) => {
		const newMap = new Map(selectedProducts);
		if (newMap.has(product.bloz07)) {
			newMap.delete(product.bloz07);
		} else {
			if (!multiSelect) newMap.clear();
			newMap.set(product.bloz07, product);
		}
		setSelectedProducts(newMap);
	};

	const handleConfirm = () => {
		onSelect(Array.from(selectedProducts.values()));
		onOpenChange(false);
	};

	const isFileImportBusy = isImportingFromFile || importMutation.isPending;
	const selectedProductsPreview = useMemo(
		() => Array.from(selectedProducts.values()).slice(0, 100),
		[selectedProducts],
	);
	const hiddenSelectedProductsCount = selectedProducts.size - selectedProductsPreview.length;

	// Kontrolki filtrow budowane raz: na desktopie w bocznym panelu, na telefonie
	// (<md, panel jest ukryty) w arkuszu „Filtry” i pod chipami.
	const hasActiveFilters =
		!!selectedProducer ||
		!!selectedSubstance ||
		!!selectedCategory ||
		selectedProductGroupId !== null;
	const clearFilters = () => {
		setSelectedProducer("");
		setSelectedSubstance("");
		setSelectedCategory(undefined);
		setSelectedProductGroupId(null);
	};
	const producerFilter = (
		<VirtualizedStringCombobox
			options={producers}
			value={selectedProducer}
			onChange={setSelectedProducer}
			placeholder="Wszyscy producenci"
			className="h-9 text-xs"
		/>
	);
	const substanceFilter = (
		<VirtualizedStringCombobox
			options={substances}
			value={selectedSubstance}
			onChange={setSelectedSubstance}
			placeholder="Wszystkie substancje"
			className="h-9 text-xs"
		/>
	);
	const categoryFilter = (
		<CategoryFilterCombobox
			options={categoryOptions}
			value={selectedCategory ?? ""}
			onChange={(v) => setSelectedCategory(v || undefined)}
			placeholder="Wszystkie kategorie"
			className="h-9 text-xs w-full"
			leavesOnly={false}
		/>
	);
	const productGroupFilter = (
		<select
			id="product-group-filter"
			aria-label="Grupa produktów"
			value={selectedProductGroupId ?? ""}
			onChange={(e) =>
				setSelectedProductGroupId(e.target.value === "" ? null : Number(e.target.value))
			}
			className="h-9 w-full rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
		>
			<option value="">Wszystkie grupy</option>
			{productGroups.map((g) => (
				<option key={g.groupId} value={g.groupId}>
					{g.name} ({g.itemCount})
				</option>
			))}
		</select>
	);
	const selectedProductsBlock = selectedProducts.size > 0 && (
		<div className="pt-4 border-t border-border">
			<div className="mb-2 flex items-center justify-between gap-2">
				<div className="text-[10px] font-bold uppercase opacity-60">
					Wybrane ({selectedProducts.size})
				</div>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-7 px-2 text-[10px] max-md:h-9 max-md:text-xs"
					onClick={() => setSelectedProducts(new Map())}
				>
					Wyczyść wybrane
				</Button>
			</div>
			<div className="space-y-1 max-h-40 overflow-y-auto">
				{selectedProductsPreview.map((p) => (
					<div
						key={p.bloz07}
						className="flex items-center justify-between bg-background p-1.5 rounded border border-border text-[10px] max-md:text-xs"
					>
						<span className="truncate flex-1 font-medium">{p.nazwa}</span>
						<button
							type="button"
							onClick={() => toggleProduct(p)}
							className="ml-1 text-muted-foreground hover:text-status-error-text max-md:-my-1 max-md:p-2"
						>
							<X className="size-3" />
						</button>
					</div>
				))}
				{hiddenSelectedProductsCount > 0 && (
					<div className="rounded border border-dashed border-border bg-muted/30 px-2 py-1.5 text-[10px] text-muted-foreground">
						Pokazano 100 z {selectedProducts.size} wybranych. Pozostale{" "}
						{hiddenSelectedProductsCount} nie sa renderowane, zeby modal pozostawal szybki.
					</div>
				)}
			</div>
		</div>
	);
	const categoryChipLabel = selectedCategory
		? (categoryOptions.find((o) => o.value === selectedCategory)?.label ?? selectedCategory)
		: "Wszystkie kategorie";
	const productGroupChipLabel =
		selectedProductGroupId === null
			? "Wszystkie grupy"
			: (productGroups.find((g) => g.groupId === selectedProductGroupId)?.name ?? "Grupa");

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-6xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden max-md:h-[calc(100dvh-0.5rem)]">
				<DialogHeader className="p-6 border-b bg-muted/20">
					<div className="flex items-center justify-between max-md:flex-col max-md:items-start max-md:gap-2">
						<div className="space-y-1">
							<DialogTitle className="flex items-center gap-2 text-xl max-md:text-base">
								<Package className="size-5 text-primary" />
								{title}
							</DialogTitle>
							<DialogDescription>
								Wyszukaj produkty po nazwie, BLOZ, producencie lub substancji czynnej.
							</DialogDescription>
						</div>
						<div className="flex items-center">
							<Button
								variant="outline"
								size="sm"
								className="gap-2 relative rounded-r-none border-r-0"
								onClick={() => document.getElementById("bloz-upload-modal")?.click()}
								disabled={isFileImportBusy}
							>
								{isFileImportBusy ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<FileUp className="size-4" />
								)}
								Zaznacz z pliku
								<input
									id="bloz-upload-modal"
									type="file"
									className="hidden"
									accept=".xlsx,.xls"
									onChange={handleFileUpload}
								/>
							</Button>
							<TooltipProvider>
								<Tooltip delayDuration={300}>
									<TooltipTrigger asChild>
										<div className="flex h-9 items-center justify-center rounded-r-md border border-input bg-background px-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-help transition-colors">
											<Info className="size-4" />
										</div>
									</TooltipTrigger>
									<TooltipContent className="max-w-xs space-y-2 p-3 text-xs" side="bottom">
										<p className="font-bold">Instrukcja zaznaczania z pliku:</p>
										<ul className="list-disc pl-4 space-y-1">
											<li>
												Akceptowane pliki: <span className="font-mono">.xlsx lub .xls</span>
											</li>
											<li>
												<span className="font-bold">Dokładnie jedna kolumna</span> z kodami — plik z
												większą liczbą kolumn zostanie odrzucony.
											</li>
											<li>
												Kod BLOZ to <span className="font-bold">7 cyfr</span>, np.{" "}
												<span className="font-bold text-primary">9102899</span>. Wszystko inne
												odrzucamy i wypisujemy w podsumowaniu — nic nie znika po cichu.
											</li>
											<li>
												Brany jest pierwszy arkusz; nagłówek kolumny może zostać. Dopasowane kody
												zaznaczą się na liście.
											</li>
										</ul>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>
					</div>
				</DialogHeader>

				{importReport && (
					<ImportReportPanel
						report={importReport}
						format={BLOZ_FORMAT}
						onDismiss={() => setImportReport(null)}
						className="m-4 mb-0 max-md:m-3 max-md:mb-0 max-md:max-h-48 max-md:shrink-0 max-md:overflow-y-auto"
					/>
				)}

				<div className="flex-1 flex overflow-hidden max-md:min-h-0 max-md:flex-col">
					<MobileFilters
						chips={[
							{
								id: "producer",
								label: "Producent (z BLOZ)",
								value: selectedProducer || "Wszyscy producenci",
								active: !!selectedProducer,
								picker: (
									<MobileFilterField label="Producent (z BLOZ)">{producerFilter}</MobileFilterField>
								),
							},
							{
								id: "substance",
								label: "Substancja (z BLOZ)",
								value: selectedSubstance || "Wszystkie substancje",
								active: !!selectedSubstance,
								picker: (
									<MobileFilterField label="Substancja (z BLOZ)">
										{substanceFilter}
									</MobileFilterField>
								),
							},
							{
								id: "category",
								label: "Kategoria",
								value: categoryChipLabel,
								active: !!selectedCategory,
								picker: <MobileFilterField label="Kategoria">{categoryFilter}</MobileFilterField>,
							},
							{
								id: "group",
								label: "Grupa produktów",
								value: productGroupChipLabel,
								active: selectedProductGroupId !== null,
								picker: (
									<MobileFilterField label="Grupa produktów">
										{productGroupFilter}
									</MobileFilterField>
								),
							},
						]}
						sheetContent={
							<>
								<MobileFilterField label="Producent (z BLOZ)">{producerFilter}</MobileFilterField>
								<MobileFilterField label="Substancja (z BLOZ)">{substanceFilter}</MobileFilterField>
								<MobileFilterField label="Kategoria">{categoryFilter}</MobileFilterField>
								<MobileFilterField label="Grupa produktów">{productGroupFilter}</MobileFilterField>
								{selectedProductsBlock}
							</>
						}
						onClear={hasActiveFilters ? clearFilters : undefined}
						resultLabel={totalCount > 0 ? `Pokaż ${totalCount} produktów` : "Pokaż produkty"}
					>
						{/* Sidebar Filtry */}
						<aside className="w-72 border-r bg-muted/10 p-4 space-y-6 overflow-y-auto hidden md:block">
							<div className="space-y-4">
								<div className="flex items-center gap-2 text-xs font-black uppercase text-muted-foreground tracking-widest">
									<Filter className="size-3" />
									Filtrowanie
								</div>

								<div className="space-y-4">
									<div className="space-y-1.5">
										<span className="text-[10px] font-bold uppercase opacity-60 block mb-1">
											Producent (z BLOZ)
										</span>
										{producerFilter}
									</div>

									<div className="space-y-1.5">
										<span className="text-[10px] font-bold uppercase opacity-60 block mb-1">
											Substancja (z BLOZ)
										</span>
										{substanceFilter}
									</div>

									<div className="space-y-1.5">
										<span className="text-[10px] font-bold uppercase opacity-60 block mb-1">
											Kategoria
										</span>
										{categoryFilter}
									</div>

									<div className="space-y-1.5">
										<label
											htmlFor="product-group-filter"
											className="text-[10px] font-bold uppercase opacity-60 block mb-1"
										>
											Grupa produktów
										</label>
										{productGroupFilter}
									</div>
								</div>
							</div>

							{hasActiveFilters && (
								<Button
									variant="ghost"
									size="sm"
									className="w-full justify-start text-[10px] h-8 text-muted-foreground hover:text-foreground"
									onClick={clearFilters}
								>
									<X className="size-3 mr-2" />
									Wyczyść filtry
								</Button>
							)}

							{selectedProductsBlock}
						</aside>
					</MobileFilters>

					{/* Glowna lista */}
					<div className="flex-1 flex flex-col min-w-0 max-md:min-h-0">
						<div className="p-4 border-b bg-card max-md:p-3">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
								<Input
									placeholder="Wpisz min. 2 znaki lub użyj =fraza dla dokładnego słowa..."
									className="pl-10 h-10"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									autoFocus
								/>
								{isFetching && (
									<div className="absolute right-3 top-1/2 -translate-y-1/2">
										<Loader2 className="size-4 animate-spin text-primary" />
									</div>
								)}
							</div>
							{isFileImportBusy && (
								<div
									className="mt-3 flex items-center gap-2 rounded-md border border-status-info-border bg-status-info-bg px-3 py-2 text-sm text-status-info-text"
									aria-live="polite"
								>
									<Loader2 className="size-4 animate-spin" />
									Ładowanie produktów z pliku...
								</div>
							)}
							{totalCount > 0 && (
								<div className="mt-2 text-[11px] text-muted-foreground">
									Załadowano{" "}
									<span className="font-semibold text-foreground">{products.length}</span>
									{" z "}
									<span className="font-semibold text-foreground">{totalCount}</span> produktów
									{hasNextPage && " — przewiń aby załadować więcej"}
								</div>
							)}
							<div className="mt-2 text-[11px] text-muted-foreground">
								Wyszukuje w nazwie, BLOZ, EAN, producencie. Użyj{" "}
								<span className="font-mono text-foreground">=fraza</span> dla dokładnego słowa (też
								w substancji czynnej), np. <span className="font-mono text-foreground">=apap</span>{" "}
								lub <span className="font-mono text-foreground">=biocodex</span>.
							</div>
						</div>

						<div className="flex-1 overflow-auto bg-muted/5 max-md:min-h-40">
							<table className="w-full border-collapse text-sm">
								<thead className="sticky top-0 z-10 bg-muted border-b border-border">
									<tr className="text-xs font-semibold text-muted-foreground">
										<th className="w-10 px-4 py-3 text-center">
											{multiSelect &&
												(isSelectingAll ? (
													<Loader2 className="size-4 animate-spin text-primary mx-auto" />
												) : (
													<input
														type="checkbox"
														className="size-4 rounded accent-primary cursor-pointer disabled:opacity-50"
														disabled={products.length === 0}
														title={
															totalCount > 0
																? `Zaznacz wszystkie ${totalCount} pasujące produkty`
																: undefined
														}
														checked={
															products.length > 0 &&
															products.every((p) => selectedProducts.has(p.bloz07))
														}
														onChange={(e) => {
															handleToggleSelectAll(e.target.checked);
														}}
													/>
												))}
										</th>
										<th className="px-4 py-3 text-left">Produkt</th>
										<th className="px-4 py-3 text-left w-24">BLOZ</th>
										<th className="px-4 py-3 text-left">Producent / Substancja</th>
										<th className="px-4 py-3 text-left w-48">Kategoria</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-border/50 bg-card">
									{products.map((p) => (
										<tr
											key={p.bloz07}
											className={cn(
												"hover:bg-muted/30 transition-colors cursor-pointer group",
												selectedProducts.has(p.bloz07) && "bg-primary/5",
											)}
											onClick={() => toggleProduct(p)}
										>
											<td
												className="px-4 py-3 text-center"
												onClick={(e) => e.stopPropagation()}
												onKeyDown={(e) => e.stopPropagation()}
											>
												<input
													type="checkbox"
													className="size-4 rounded accent-primary cursor-pointer"
													checked={selectedProducts.has(p.bloz07)}
													onChange={() => toggleProduct(p)}
												/>
											</td>
											<td className="px-4 py-3">
												<div className="flex flex-col max-w-[400px] max-md:max-w-[13rem]">
													<span
														className="font-medium text-foreground group-hover:text-primary transition-colors truncate"
														title={p.nazwa}
													>
														{p.nazwa}
													</span>
													<div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase mt-0.5">
														<span>{p.dawka}</span>
														<span>{p.postc}</span>
														{p.inPharmacy && (
															<Badge className="text-[9px] py-0 h-3.5 bg-status-success/10 text-status-success-text border-status-success/20">
																W aptece
															</Badge>
														)}
													</div>
												</div>
											</td>
											<td className="px-4 py-3 font-mono text-xs text-muted-foreground">
												{p.bloz07}
											</td>
											<td className="px-4 py-3">
												<div className="flex flex-col gap-1 max-w-[250px]">
													<span
														className="text-[10px] font-bold text-foreground/70 truncate"
														title={p.prodc || ""}
													>
														{p.prodc || "Brak producenta"}
													</span>
													<span
														className="text-[10px] text-muted-foreground italic truncate"
														title={p.nzwmi || ""}
													>
														{p.nzwmi || "Brak substancji"}
													</span>
												</div>
											</td>
											<td className="px-4 py-3">
												{p.primaryCategoryLabel ? (
													<div className="flex flex-col gap-0.5 max-w-[192px]">
														<span
															className="text-[10px] font-medium text-foreground/80 truncate"
															title={`${p.primaryCategoryCode ?? ""} ${p.primaryCategoryLabel}`.trim()}
														>
															{p.primaryCategoryLabel}
														</span>
														<span className="text-[9px] font-mono text-muted-foreground">
															{p.primaryCategoryCode}
														</span>
													</div>
												) : (
													<span className="text-[10px] text-muted-foreground italic">Brak</span>
												)}
											</td>
										</tr>
									))}
									{/* Sentinel row dla IntersectionObserver — triggeruje fetchNextPage */}
									{hasNextPage && (
										<tr ref={sentinelRef}>
											<td colSpan={5} className="h-1 p-0" />
										</tr>
									)}
									{isFetchingNextPage && (
										<tr>
											<td
												colSpan={5}
												className="py-3 text-center text-xs text-muted-foreground italic"
											>
												Ładowanie więcej...
											</td>
										</tr>
									)}
									{products.length === 0 && !isFetching && (
										<tr>
											<td colSpan={5} className="py-20 text-center text-muted-foreground italic">
												{debouncedQuery.length < 2 &&
												!selectedProducer &&
												!selectedSubstance &&
												!selectedCategory
													? "Zacznij wpisywać nazwę lub wybierz filtry..."
													: "Nie znaleziono produktów spełniających kryteria."}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>

				<DialogFooter className="p-4 border-t bg-muted/20 sm:justify-between items-center">
					<div className="text-xs text-muted-foreground max-md:w-full">
						Wybrano: <span className="font-bold text-foreground">{selectedProducts.size}</span>{" "}
						produktów
					</div>
					<div className="flex items-center gap-3 max-md:w-full max-md:gap-2 max-md:[&>button]:h-11 max-md:[&>button]:min-w-0 max-md:[&>button]:flex-1">
						<Button variant="ghost" onClick={() => onOpenChange(false)}>
							Anuluj
						</Button>
						<Button
							disabled={selectedProducts.size === 0}
							onClick={handleConfirm}
							className="gap-2 min-w-[140px]"
						>
							<CheckCircle2 className="size-4" />
							Dodaj zaznaczone
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
